import React, { useState, useEffect } from 'react';
import '../styles/Services.css';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('registration');
  const [expandedService, setExpandedService] = useState(null);
  const [servicesData, setServicesData] = useState({});
  const navigate = useNavigate();

  const location = useLocation();

  const categories = [
    { id: 'registration', name: 'Registration/Licences', icon: '📄' },
    { id: 'filings', name: 'Filings', icon: '💼' },
    { id: 'auditing', name: 'Auditing', icon: '✓' },
    { id: 'certification', name: 'Certification', icon: '🎓' }
  ];

  // Fetch services from backend
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_SERVICES_API}`); // change base URL if needed
        if (res.data.success) {
          // Transform array into object grouped by category
          const grouped = res.data.services.reduce((acc, service) => {
            if (!acc[service.category]) acc[service.category] = [];
            acc[service.category].push({
              id: service.service_key || service.id,
              title: service.title,
              time: service.time_required,
              description: service.description,
              details: JSON.parse(service.details || '{}')
            });
            return acc;
          }, {});
          setServicesData(grouped);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };

    fetchServices();
  }, []);

  // Handle category from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    const service = params.get('service');

    if (category && servicesData[category]) {
      setSelectedCategory(category);
    }

    if (service) {
      setExpandedService(service);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.search, servicesData]);

  const toggleService = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (window.innerWidth <= 768) {
      const servicesGrid = document.querySelector('.services-grid');
      if (servicesGrid) {
        servicesGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  if (!servicesData[selectedCategory]) {
    return <div className="services-container">Loading services...</div>;
  }

  return (
    <div className="services-container">
      <div className="services-wrapper">
        <div className="services-header">
          <h1 className="services-title">Our Services</h1>
          <p className="services-subtitle">
            Comprehensive CA services for all your business compliance and growth needs
          </p>
        </div>

        <div className="category-tabs">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-tab ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category.id)}
              aria-label={`View ${category.name} services`}
            >
              <span className="category-icon" role="img" aria-label={category.name}>
                {category.icon}
              </span>
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        <div className="services-grid">
          {servicesData[selectedCategory].map((service) => (
            <div key={service.id} className="service-card">
              <div className="service-header">
                <h3 className="service-title">{service.title}</h3>
                <span className="service-time">{service.time}</span>
              </div>

              <p className="service-description">{service.description}</p>

              <button
                className="learn-more-btn"
                onClick={() => toggleService(service.id)}
                aria-expanded={expandedService === service.id}
                aria-controls={`service-details-${service.id}`}
              >
                <span>{expandedService === service.id ? 'Show Less' : 'Learn More'}</span>
                <span>{expandedService === service.id ? '▲' : '▼'}</span>
              </button>

              {expandedService === service.id && (
                <div
                  className="service-details"
                  id={`service-details-${service.id}`}
                  role="region"
                  aria-label={`Details for ${service.title}`}
                >
                  {Object.entries(service.details).map(([key, value]) => {
                    if (Array.isArray(value)) {
                      const titleMap = {
                        who: 'Who needs it?',
                        documents: 'Documents Required',
                        types: 'Types',
                        benefits: 'Benefits',
                        uses: 'Used For',
                        includes: 'Includes',
                        verified: 'Data Verified'
                      };
                      return (
                        <div className="detail-section" key={key}>
                          <h4 className="detail-title">{titleMap[key] || key}</h4>
                          <ul className="detail-list">
                            {value.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    } else {
                      return (
                        <div className="detail-section" key={key}>
                          <h4 className="detail-title">{key === 'what' ? 'What is it?' : key}</h4>
                          <p className="detail-text">{value}</p>
                        </div>
                      );
                    }
                  })}
          <button
            className="get-started-btn"
            onClick={() => navigate('/contact')}
          >
            Get Started →
          </button>                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Services;
